import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { type Proposal, generateProposal, listProposals } from "../api/crm.ts";

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "text-gray-300 bg-gray-700",
  GENERATING: "text-yellow-400 bg-yellow-900/30",
  SUBMITTED: "text-blue-400 bg-blue-900/30",
  APPROVED: "text-green-400 bg-green-900/30",
  REJECTED: "text-red-400 bg-red-900/30",
  ARCHIVED: "text-gray-500 bg-gray-800",
};

export function ProposalsPage() {
  const { token } = useAuth();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [generating, setGenerating] = useState<string | null>(null);
  const limit = 20;

  function load() {
    if (token == null) return;
    setLoading(true);
    listProposals(token, { status: status || undefined, limit, offset: page * limit })
      .then((res) => { setProposals(res.items); setTotal(res.total); })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load proposals"))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [token, status, page]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function handleGenerate(p: Proposal) {
    if (token == null) return;
    setGenerating(p.id);
    try {
      await generateProposal(token, p.id, p.version);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to queue generation");
    } finally {
      setGenerating(null);
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Proposals</h1>
          <span className="text-sm text-gray-500">{total} total</span>
        </div>

        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="">All statuses</option>
          {["DRAFT", "GENERATING", "SUBMITTED", "APPROVED", "REJECTED"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}
        {!loading && proposals.length === 0 && <p className="text-gray-500">No proposals found.</p>}

        <div className="space-y-2">
          {proposals.map((p) => (
            <div key={p.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{p.title}</p>
                  <div className="flex gap-3 mt-1 text-xs text-gray-500">
                    <span>v{p.proposalVersion}</span>
                    {p.estimatedValue != null && <span>${p.estimatedValue.toLocaleString()} {p.currency}</span>}
                    {p.validUntil && <span>valid until {new Date(p.validUntil).toLocaleDateString()}</span>}
                    <span>{new Date(p.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.status === "DRAFT" && (
                    <button
                      onClick={() => void handleGenerate(p)}
                      disabled={generating === p.id}
                      className="text-xs px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 transition-colors"
                    >
                      {generating === p.id ? "Queuing…" : "Generate AI Draft"}
                    </button>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUS_COLORS[p.status] ?? "text-gray-400 bg-gray-800"}`}>
                    {p.status}
                  </span>
                </div>
              </div>
              {p.status === "GENERATING" && (
                <p className="mt-2 text-xs text-yellow-400/80">AI generation in progress — human approval required before content is applied.</p>
              )}
            </div>
          ))}
        </div>

        {total > limit && (
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Previous</button>
            <span className="text-sm text-gray-500 self-center">{page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}</span>
            <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= total} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </Layout>
  );
}
